import { List } from "immutable";
import { createAction } from "redux-actions";
import * as AlertActions from "./NotificationActions";
import { Thunk } from "./Thunk";
import * as M from "../models";
import { Api } from "../services/Api";

const prefix = "ADMIN_ABSTRACT_";

export class Constants {
    public static LOADING_ABSTRACT = prefix + "LOADING_ABSTRACT";
    public static LOADING_LIST_ABSTRACTS = prefix + "LOADING_LIST_ABSTRACTS";
    public static LOADING_EVENT = prefix + "LOADING_EVENT";
    public static LOADING_FDS = prefix + "LOADING_FDS";
    public static LOADING_GROUP = prefix + "LOADING_GROUP";
    public static LOADING_GROUPS = prefix + "LOADING_GROUPS";
    public static LOADING_TYPE = prefix + "LOADING_TYPE";
    public static LOADING_TYPES = prefix + "LOADING_TYPES";
    public static UPDATED_ABSTRACTS = prefix + "UPDATED_ABSTRACTS";
    public static UPDATED_EVENTS = prefix + "UPDATED_EVENTS";
    public static UPDATED_FDS = prefix + "UPDATED_FDS";
    public static UPDATED_GROUPS = prefix + "UPDATED_GROUPS";
    public static UPDATED_LIST_ABSTRACTS = prefix + "UPDATED_LIST_ABSTRACTS";
    public static UPDATED_TYPES = prefix + "UPDATED_TYPES";
}

interface LoadingAbstractPayload {
    abstractId: number | string;
    loading: boolean;
}

interface LoadingForEventPayload {
    eventId: number | string;
    loading: boolean;
}

interface LoadingGroupPayload {
    groupId: number | string;
    loading: boolean;
}

interface LoadingTypePayload {
    typeId: number | string;
    loading: boolean;
}

export const loadAbstract = (guid: string): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const result = await api.get(Api.endpoints.abstract.abstract.withUrlParams({ guid }));

    if (result.isSuccess && result.data)
        dispatch(updateAbstract(result.data));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingAbstract(guid, false));
};

export const loadFinancialDisclosures = (eventId: number): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const qp = { queryParams: { eventId }};
    const result = await api.get(Api.endpoints.admin.abstract.financialDisclosures, qp);

    if (result.isSuccess && result.data)
        dispatch(updateFDs(result.data));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingFDs(false));
};

export const loadGroup = (id: number): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const result = await api.get(Api.endpoints.admin.abstractGroup.group.withUrlParams({ id }));

    if (result.isSuccess && result.data)
        dispatch(updateGroup(result.data));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingGroup(id, false));
};

export const loadGroupsForEvent = (eventId: number): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const qp = { queryParams: { eventId }};
    const result = await api.get(Api.endpoints.admin.abstractGroup.list, qp);

    if (result.isSuccess && result.data)
        dispatch(updateGroups(result.data));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingGroups(eventId, false));
};

export const loadListAbstractsForEvent = (eventId: number): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const qp = { queryParams: { eventId }};
    const result = await api.get(Api.endpoints.admin.abstract.list, qp);

    if (result.isSuccess && result.data)
        dispatch(replaceListAbstracts(result.data, eventId));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingListAbstracts(false));
};

export const loadEvent = (eventId: number): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const qp = { queryParams: { eventId }};
    const result = await api.get(Api.endpoints.admin.abstract.event, qp);

    if (result.isSuccess && result.data)
        dispatch(updateEvent(result.data));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingEvent(eventId, false));
};

export const loadType = (typeId: number): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const qp = { queryParams: { forGrading: true }};
    const result = await api.get(Api.endpoints.abstract.type.withUrlParams({ id: typeId }), qp);

    if (result.isSuccess && result.data)
        dispatch(updateType(result.data));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingType(typeId, false));
};

export const loadTypes = (eventId: number): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const qp = { queryParams: { includeForm: true }};
    const result = await api.get(Api.endpoints.abstract.types.withUrlParams({ eventId }), qp);

    if (result.isSuccess && result.data)
        dispatch(updateTypes(result.data));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingTypes(false));
};

export const removeAbstract = (abstract: M.Abstract.Abstract): Thunk => async (dispatch, getState) => {
    const abstracts = getState().adminAbstracts.abstracts;
    const idx = abstracts.findIndex((x: M.Abstract.Abstract) => x.guid === abstract.guid);
    if (idx > -1)
        dispatch(onAbstractsUpdated(abstracts.remove(idx)));

    // also remove from abstract list
    const listAbstracts = getState().adminAbstracts.listAbstracts;
    const listIdx = listAbstracts.findIndex((x: M.Abstract.AbstractListItem) => x.guid === abstract.guid);
    if (listIdx > -1)
        dispatch(onListAbstractsUpdated(listAbstracts.remove(listIdx)));
};

export const removeAbstracts = (abstractGuids: string[]): Thunk => async (dispatch, getState) => {
    let abstracts = getState().adminAbstracts.abstracts;
    let listAbstracts = getState().adminAbstracts.listAbstracts;

    for (const guid of abstractGuids) {
        const idx = abstracts.findIndex((x: M.Abstract.Abstract) => x.guid === guid);
        const listIdx = listAbstracts.findIndex((x: M.Abstract.AbstractListItem) => x.guid === guid);
        if (idx > -1)
            abstracts = abstracts.remove(idx);
        if (listIdx > -1)
            listAbstracts = listAbstracts.remove(listIdx);
    }

    dispatch(onAbstractsUpdated(abstracts));
    dispatch(onListAbstractsUpdated(listAbstracts));
};

export const removeGroup = (group: M.Abstract.GradingGroup): Thunk => async (dispatch, getState) => {
    const groups = getState().adminAbstracts.groups;
    const idx = groups.findIndex((x: M.Abstract.GradingGroup) => x.id === group.id);
    if (idx > -1)
        dispatch(onGroupsUpdated(groups.remove(idx)));
};

export const replaceListAbstracts = (updatedList: M.Abstract.AbstractListItem[], eventId: number): Thunk => async (dispatch, getState) => {
    let list = getState().adminAbstracts.listAbstracts
        .filter((item: M.Abstract.AbstractListItem) => item.eventId !== eventId)
        .toList();

    for (const item of updatedList)
        list = list.push(item);

    dispatch(onListAbstractsUpdated(list));
};

export const updateAbstract = (a: M.Abstract.Abstract): Thunk => async (dispatch, getState) => {
    dispatch(updateAbstracts([a]));
};

export const updateAbstracts = (updatedAbstracts: M.Abstract.Abstract[] | List<M.Abstract.Abstract>): Thunk => async (dispatch, getState) => {
    let abstracts = getState().adminAbstracts.abstracts;
    const _updatedAbstracts: M.Abstract.Abstract[] = !Array.isArray(updatedAbstracts)
        ? updatedAbstracts.toArray()
        : updatedAbstracts;

    _updatedAbstracts.map((e: M.Abstract.Abstract) => {
        const idx = abstracts.findIndex((x: M.Abstract.Abstract) => x.id === e.id);
        abstracts = idx > -1
            ? abstracts.set(idx, e)
            : abstracts.push(e);
    });

    dispatch(onAbstractsUpdated(abstracts));
};

export const updateEvent = (evt: M.Abstract.AbstractEvent): Thunk => async (dispatch, getState) => {
    dispatch(updateEvents([evt]));
};

export const updateEvents = (updatedEvents: M.Abstract.AbstractEvent[] | List<M.Abstract.AbstractEvent>): Thunk => async (dispatch, getState) => {
    let events = getState().adminAbstracts.events;
    const _updatedEvents: M.Abstract.AbstractEvent[] = !Array.isArray(updatedEvents)
        ? updatedEvents.toArray()
        : updatedEvents;

    _updatedEvents.map((e: M.Abstract.AbstractEvent) => {
        const idx = events.findIndex((x: M.Abstract.AbstractEvent) => x.eventId === e.eventId);
        events = idx > -1
            ? events.set(idx, e)
            : events.push(e);
    });

    dispatch(onEventsUpdated(events));
};

export const updateFD = (fd: M.FinancialDisclosure.FD): Thunk => async (dispatch, getState) => {
    dispatch(updateFDs([fd]));
};

export const updateFDs = (updatedFDs: M.FinancialDisclosure.FD[] | List<M.FinancialDisclosure.FD>): Thunk => async (dispatch, getState) => {
    let FDs = getState().adminAbstracts.FDs;
    const _updatedFDs: M.FinancialDisclosure.FD[] = !Array.isArray(updatedFDs)
        ? updatedFDs.toArray()
        : updatedFDs;

    _updatedFDs.map((m: M.FinancialDisclosure.FD) => {
        const idx = FDs.findIndex((x: M.FinancialDisclosure.FD) => x.id === m.id);
        FDs = idx > -1
            ? FDs.set(idx, m)
            : FDs.push(m);
    });

    dispatch(onFDsUpdated(FDs));
};

export const updateGroup = (group: M.Abstract.GradingGroup): Thunk => async (dispatch, getState) => {
    dispatch(updateGroups([group]));
};

export const updateGroups = (updatedGroups: M.Abstract.GradingGroup[] | List<M.Abstract.GradingGroup>): Thunk => async (dispatch, getState) => {
    let groups = getState().adminAbstracts.groups;
    const _updatedGroups: M.Abstract.GradingGroup[] = !Array.isArray(updatedGroups)
        ? updatedGroups.toArray()
        : updatedGroups;

    _updatedGroups.map((e: M.Abstract.GradingGroup) => {
        const idx = groups.findIndex((x: M.Abstract.GradingGroup) => x.id === e.id);
        groups = idx > -1
            ? groups.set(idx, e)
            : groups.push(e);
    });

    dispatch(onGroupsUpdated(groups));
};

export const updateListAbstract = (a: M.Abstract.AbstractListItem): Thunk => async (dispatch, getState) => {
    dispatch(updateListAbstracts([a]));
};

export const updateListAbstracts = (updatedAbstracts: M.Abstract.AbstractListItem[] | List<M.Abstract.AbstractListItem>): Thunk => async (dispatch, getState) => {
    let abstracts = getState().adminAbstracts.listAbstracts;
    const _updatedAbstracts: M.Abstract.AbstractListItem[] = !Array.isArray(updatedAbstracts)
        ? updatedAbstracts.toArray()
        : updatedAbstracts;

    _updatedAbstracts.map((e: M.Abstract.AbstractListItem) => {
        const idx = abstracts.findIndex((x: M.Abstract.AbstractListItem) => x.id === e.id);
        abstracts = idx > -1
            ? abstracts.set(idx, e)
            : abstracts.push(e);
    });

    dispatch(onListAbstractsUpdated(abstracts));
};

export const updateType = (type: M.Abstract.AbstractType): Thunk => async (dispatch, getState) => {
    dispatch(updateTypes([type]));
};

export const updateTypes = (updatedTypes: M.Abstract.AbstractType[] | List<M.Abstract.AbstractType>): Thunk => async (dispatch, getState) => {
    let types = getState().adminAbstracts.types;
    const _updatedTypes: M.Abstract.AbstractType[] = !Array.isArray(updatedTypes)
        ? updatedTypes.toArray()
        : updatedTypes;

    _updatedTypes.map((e: M.Abstract.AbstractType) => {
        const idx = types.findIndex((x: M.Abstract.AbstractType) => x.id === e.id);
        types = idx > -1
            ? types.set(idx, e)
            : types.push(e);
    });

    dispatch(onTypesUpdated(types));
};

export const onAbstractsUpdated = createAction<List<M.Abstract.Abstract>, List<M.Abstract.Abstract>>(
    Constants.UPDATED_ABSTRACTS, (abstracts) => (abstracts),
);

export const onEventsUpdated = createAction<List<M.Abstract.AbstractEvent>, List<M.Abstract.AbstractEvent>>(
    Constants.UPDATED_EVENTS, (events) => (events),
);

export const onFDsUpdated = createAction<List<M.FinancialDisclosure.FD>, List<M.FinancialDisclosure.FD>>(
    Constants.UPDATED_FDS, (events) => (events),
);

export const onListAbstractsUpdated = createAction<List<M.Abstract.AbstractListItem>, List<M.Abstract.AbstractListItem>>(
    Constants.UPDATED_LIST_ABSTRACTS, (abstracts) => (abstracts),
);

export const onGroupsUpdated = createAction<List<M.Abstract.GradingGroup>, List<M.Abstract.GradingGroup>>(
    Constants.UPDATED_GROUPS, (groups) => (groups),
);

export const onTypesUpdated = createAction<List<M.Abstract.AbstractType>, List<M.Abstract.AbstractType>>(
    Constants.UPDATED_TYPES, (types) => (types),
);

export const toggleLoadingAbstract = createAction<LoadingAbstractPayload, number | string, boolean>(
    Constants.LOADING_ABSTRACT, (abstractId, loading) => ({ abstractId, loading }),
);

export const toggleLoadingListAbstracts = createAction<boolean, boolean>(
    Constants.LOADING_LIST_ABSTRACTS, (loading) => (loading),
);

export const toggleLoadingEvent = createAction<LoadingForEventPayload, number | string, boolean>(
    Constants.LOADING_EVENT, (eventId, loading) => ({ eventId, loading }),
);

export const toggleLoadingFDs = createAction<boolean, boolean>(
    Constants.LOADING_FDS, (loading) => (loading),
);

export const toggleLoadingGroup = createAction<LoadingGroupPayload, number | string, boolean>(
    Constants.LOADING_GROUP, (groupId, loading) => ({ groupId, loading }),
);

export const toggleLoadingGroups = createAction<LoadingForEventPayload, number | string, boolean>(
    Constants.LOADING_GROUPS, (eventId, loading) => ({ eventId, loading }),
);

export const toggleLoadingType = createAction<LoadingTypePayload, number | string, boolean>(
    Constants.LOADING_TYPE, (typeId, loading) => ({ typeId, loading }),
);

export const toggleLoadingTypes = createAction<boolean, boolean>(
    Constants.LOADING_TYPES, (loading) => (loading),
);



// WEBPACK FOOTER //
// ./src/actions/AdminAbstractActions.ts